"use client";

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import Image from "next/image";
import { cn } from "@/lib/utils";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { motion, useMotionValue } from "framer-motion";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import { CTACornerButton } from "@/shadcn/ui/corner-button";

import { PageAdLongSchema } from "@/sanity/pages/PageAdLongTemplate";

import dots from "@/public/vsl-short/dots.svg";

// card accent colors from Figma, cycled when an item has no explicit color
const ACCENT_COLORS = ["#D9F9B9", "#B9C0F9", "#F9E0B9", "#E9B9F9", "#B9F0F9"];

export default function StepsSection({
  data,
  className,
}: {
  data: PageAdLongSchema;
  className?: string;
}) {
  const items = data?.stepsItems ?? [];

  // desktop: the section pins while the card track slides horizontally
  const sectionRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLUListElement>(null);

  // scrollRange = how far the track must travel to reveal its last card
  // pinHeight = natural height of the locked screen, so the runway below it is exact
  const [scrollRange, setScrollRange] = useState(0);
  const [pinHeight, setPinHeight] = useState(0);
  const [current, setCurrent] = useState(0);

  const x = useMotionValue(0);

  const scheduled = useRef(false);
  const scheduleRecalc = useCallback(() => {
    if (scheduled.current) return;
    scheduled.current = true;
    requestAnimationFrame(() => {
      scheduled.current = false;
      const viewportWidth = viewportRef.current?.clientWidth ?? 0;
      const trackWidth = trackRef.current?.scrollWidth ?? 0;
      setScrollRange(Math.max(0, trackWidth - viewportWidth));
      setPinHeight(viewportRef.current?.offsetHeight ?? 0);
    });
  }, []);

  useLayoutEffect(() => {
    scheduleRecalc();
  }, [items.length, scheduleRecalc]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const observer = new ResizeObserver(() => scheduleRecalc());
    observer.observe(viewport);
    observer.observe(track);

    window.addEventListener("resize", scheduleRecalc, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", scheduleRecalc);
    };
  }, [scheduleRecalc]);

  /*
    While the section is pinned its top edge sits at the viewport top, so its
    own offset from the top is exactly how far it has been scrolled through —
    0 at the start of the pin, -scrollRange at the end. No page offsets or
    viewport-height math needed, which keeps it correct after any layout shift.
  */
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    let frame = 0;

    const update = () => {
      frame = 0;
      if (scrollRange <= 0) {
        x.set(0);
        setCurrent(0);
        return;
      }
      const travelled = Math.min(
        Math.max(-section.getBoundingClientRect().top, 0),
        scrollRange,
      );
      x.set(-travelled);
      setCurrent(
        Math.round((travelled / scrollRange) * Math.max(0, items.length - 1)),
      );
    };

    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [scrollRange, items.length, x]);

  // one card, shared by the mobile stack and the desktop carousel
  const renderCard = (item: (typeof items)[number], index: number) => {
    const accent = item?.color || ACCENT_COLORS[index % ACCENT_COLORS.length];
    const number = String(index + 1).padStart(2, "0");

    return (
      <div className="flex flex-col w-full h-full">
        {/* accent top bar */}
        <div
          className="w-full h-[0.5rem]"
          style={{ backgroundColor: accent }}
        />

        <div className="flex flex-col lg:flex-row flex-1 bg-white lg:h-[43.75rem]">
          {/* number panel — layered ghost copies behind the number, like the Figma echo effect */}
          <div className="flex relative flex-col justify-end items-start w-full h-[20rem] shrink-0 overflow-clip bg-[#0A1A1D] pt-[1.6rem] pb-[1rem] pl-[1.6rem] lg:h-auto lg:w-[34.875rem] lg:pt-[1.75rem] lg:pl-[1.75rem]">
            {/* position dots — desktop highlights the card currently scrolled into view */}
            <div className="hidden lg:flex absolute items-center gap-[0.75rem] top-[1.75rem] left-[1.75rem]">
              {items.map((_, dotIndex) => (
                <span
                  key={dotIndex}
                  className="h-[1.375rem] w-[1.375rem]"
                  style={{
                    backgroundColor:
                      dotIndex === current ? accent : `${accent}33`,
                  }}
                />
              ))}
            </div>
            {/* position dots — mobile stack highlights this card's own step */}
            <div className="flex lg:hidden absolute items-center gap-[0.6rem] top-[1.6rem] left-[1.6rem]">
              {items.map((_, dotIndex) => (
                <span
                  key={dotIndex}
                  className="h-[1.1rem] w-[1.1rem]"
                  style={{
                    backgroundColor:
                      dotIndex === index ? accent : `${accent}33`,
                  }}
                />
              ))}
            </div>

            {/*
              Number stack: the solid copy sits in normal flow so `justify-end` on the panel
              anchors it to the bottom — `bottom-*` is unreliable for absolute children here.
              The two faded copies are pulled upward from the same top edge and painted first,
              so each lower copy's dark background masks the one above it (the Figma echo).
            */}
            <div className="relative w-fit">
              {[
                {
                  color: `${accent}38`,
                  shift: "-translate-y-[3.5rem]",
                },
                {
                  color: `${accent}7A`,
                  shift: "-translate-y-[1.875rem]",
                },
              ].map((ghost, ghostIndex) => (
                <p
                  key={ghostIndex}
                  className={cn(
                    "absolute top-0 left-0 m-0 bg-[#0A1A1D] py-[0.625rem] font-funnel font-medium whitespace-nowrap text-[12.5rem] lg:text-[18.75rem] leading-none tracking-[-0.04em] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic]",
                    ghost.shift,
                  )}
                  style={{ color: ghost.color }}
                >
                  {number}
                </p>
              ))}
              <p
                className="relative m-0 font-medium leading-none whitespace-nowrap bg-[#0A1A1D] py-[0.625rem] font-funnel text-[12.5rem] tracking-[-0.04em] [text-box-trim:trim-both] [text-box-edge:cap_alphabetic] lg:text-[18.75rem]"
                style={{ color: accent }}
              >
                {number}
              </p>
            </div>
          </div>

          {/* content panel */}
          <div className="flex flex-col flex-1 justify-between gap-[6rem] px-[1.3rem] py-[2rem] lg:gap-[5rem] lg:p-[5rem]">
            {item?.title && (
              <p className="m-0 font-funnel font-medium leading-none text-black text-[3.5rem] lg:text-[5rem] w-[min(42.75rem,100%)]">
                {item.title}
              </p>
            )}

            {item?.list && item.list.length > 0 && (
              <ul className="flex flex-col gap-[0.5rem] w-full">
                {item.list.map((entry, entryIndex) => (
                  <li key={entryIndex} className="flex items-stretch">
                    <span
                      className="flex justify-center items-center w-[3.2rem] shrink-0 p-[0.8rem] lg:w-[4rem] lg:p-[1rem]"
                      style={{ backgroundColor: accent }}
                    >
                      {entry?.icon?.src && (
                        <Image
                          className="object-contain !h-[2.5rem] !w-[2.5rem]"
                          src={entry.icon}
                          alt=""
                        />
                      )}
                    </span>
                    <span className="flex flex-1 items-center bg-[#F3F2F1] p-[0.8rem] lg:p-[1rem]">
                      <span className="font-geist text-[2.25rem] leading-[1.5] text-black/80">
                        {entry?.text}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className={cn("relative bg-[#F3F2F1]", className)}>
      {/*
        On desktop this wrapper adds the vertical runway the pin needs: its height is
        the locked screen plus the track's horizontal overflow, so scrolling through
        the runway moves the cards exactly to their end. On mobile it stays auto.
      */}
      <div
        ref={sectionRef}
        className="relative w-full lg:h-[var(--steps-runway)]"
        style={
          {
            "--steps-runway": pinHeight
              ? `${pinHeight + scrollRange}px`
              : "auto",
          } as React.CSSProperties
        }
      >
        {/* the locked screen — keeps its normal layout, just held in place */}
        <div
          ref={viewportRef}
          className="w-full lg:sticky lg:top-0 lg:overflow-hidden"
        >
          {/* header — side lines run from the section top down to the divider below it */}
          <div className="flex relative flex-col items-center text-center px-[2rem] pt-[15.75rem] pb-[5.625rem] sm:px-[3rem] md:px-[5rem]">
            <Image
              src={dots}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 !w-full lg:!w-[119.875rem] select-none"
            />

            <DecorationLine
              className="top-0 left-[1rem] lg:left-[8.85%]"
              orientation="vertical"
              style="dark"
            />
            <DecorationLine
              className="top-0 right-[1rem] lg:right-[8.85%]"
              orientation="vertical"
              style="dark"
            />
            <DecorationLine
              className="bottom-0 hidden lg:block"
              orientation="horizontal"
              style="dark"
            />

            {data?.stepsChip?.text && (
              <Chip
                className="mb-[2.75rem] [&_p]:uppercase [&_p]:font-geistmono [&_p]:text-[#202223] relative z-[1]"
                image={data?.stepsChip?.icon}
                text={data?.stepsChip?.text}
              />
            )}

            {data?.stepsTitle && (
              <div className="pixel-highlight richtext richtext-center w-[min(83rem,100%)] relative z-[1] prose-em:text-[4rem] sm:prose-em:text-[5.5rem] lg:prose-em:text-[6.5rem] prose-em:font-ivy prose-em:!px-0 lg:prose-em:!px-[.7rem] prose-headings:font-funnel prose-headings:font-medium prose-headings:text-black prose-headings:text-[4rem] sm:prose-headings:text-[5.5rem] lg:prose-headings:text-[6.5rem] prose-headings:leading-none prose-headings:text-balance">
                <PortableText
                  value={data.stepsTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>

          {items.length > 0 && (
            <>
              {/* mobile — plain vertical stack (no horizontal scroll) */}
              <div className="relative flex flex-col gap-[1.6rem] px-[3rem] lg:hidden">
                <DecorationLine
                  className="top-0 left-[1rem]"
                  orientation="vertical"
                  style="dark"
                />
                <DecorationLine
                  className="top-0 right-[1rem]"
                  orientation="vertical"
                  style="dark"
                />
                {items.map((item, index) => (
                  <React.Fragment key={index}>
                    {renderCard(item, index)}
                  </React.Fragment>
                ))}
              </div>

              {/*
                desktop — the card track slides horizontally as the section stays locked.
                Clipping lives here rather than on the locked screen so only the track's
                horizontal overflow is hidden and the CTA below can never be cut off.
              */}
              <div className="hidden lg:block overflow-hidden mt-[6.125rem]">
                <motion.ul
                  ref={trackRef}
                  style={{ x }}
                  className="flex items-stretch w-max gap-[3.25rem] pl-[40rem] pr-[30rem] my-0 list-none will-change-transform"
                >
                  {items.map((item, index) => (
                    <li key={index} className="shrink-0 w-[min(101.25rem,92vw)]">
                      {renderCard(item, index)}
                    </li>
                  ))}
                </motion.ul>
              </div>
            </>
          )}

          {/* CTA — part of the locked screen, so it stays put with the header and cards */}
          {data?.stepsCTA && (
            <div className="relative flex justify-center pt-[5rem] pb-[7rem] px-[3rem] lg:px-0">
              <DecorationLine
                className="top-0 left-[1rem] lg:left-[8.85%] lg:hidden"
                orientation="vertical"
                style="dark"
              />
              <DecorationLine
                className="top-0 right-[1rem] lg:right-[8.85%] lg:hidden"
                orientation="vertical"
                style="dark"
              />
              <CTACornerButton data={data.stepsCTA} />
            </div>
          )}
        </div>
      </div>

      <DecorationLine
        className="bottom-0"
        orientation="horizontal"
        style="dark"
      />
    </section>
  );
}
